import './ManageAuthors.css';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchAuthors, deleteAuthor, searchAuthors } from '../../services/authorService';
import Notification from '../Notification/Notification';
import Pagination from '../Pagination/Pagination';

const AUTHORS_PER_PAGE = 12;

function ManageAuthors() {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [authors, setAuthors] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [showNotification, setShowNotification] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState('');

    // Check for success message from navigation
    useEffect(() => {
        if (location.state?.message) {
            setNotificationMessage(location.state.message);
            setShowNotification(true);
            // Clear the state so notification doesn't show again on refresh
            window.history.replaceState({}, document.title);
        }
    }, [location]);

    // Debounce search term
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
            setCurrentPage(0); // Reset to first page on new search
        }, 500);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    const loadAuthors = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            let pageData;
            if (debouncedSearchTerm.trim()) {
                // Use search endpoint if there's a search term
                pageData = await searchAuthors(debouncedSearchTerm.trim(), currentPage, AUTHORS_PER_PAGE);
            } else {
                // Use regular fetch if no search term
                pageData = await fetchAuthors(currentPage, AUTHORS_PER_PAGE);
            }

            setAuthors(pageData.content);
            setTotalPages(pageData.page.totalPages);
            setTotalElements(pageData.page.totalElements);
        } catch (err) {
            setError('Nepodařilo se načíst autory. Zkuste to prosím znovu.');
            console.error('Error loading authors:', err);
        } finally {
            setLoading(false);
        }
    }, [currentPage, debouncedSearchTerm]);

    useEffect(() => {
        loadAuthors();
    }, [loadAuthors]);

    const handlePageChange = (newPage) => {
        if (newPage >= 0 && newPage < totalPages) {
            setCurrentPage(newPage);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handleBack = () => {
        navigate('/admin');
    };

    const handleEdit = (authorId) => {
        navigate(`/admin/edit-author/${authorId}`);
    };

    const handleDeleteClick = (author) => {
        setDeleteConfirm(author);
    };

    const handleDeleteCancel = () => {
        setDeleteConfirm(null);
    };

    const handleDeleteConfirm = async () => {
        if (!deleteConfirm) return;

        try {
            setDeleting(true);
            await deleteAuthor(deleteConfirm.id);

            setDeleteConfirm(null);

            // Reload the current page to get updated data
            await loadAuthors();
        } catch (err) {
            setError('Nepodařilo se smazat autora. Zkuste to prosím znovu.');
            console.error('Error deleting author:', err);
        } finally {
            setDeleting(false);
        }
    };

    const handleCloseNotification = () => {
        setShowNotification(false);
    };

    return (
        <div className="admin-page">
            <div className="manage-container">
                <button className="back-button" onClick={handleBack}>
                    ← Zpět na dashboard
                </button>

                <h1 className="page-title">Spravovat autory</h1>

                {showNotification && (
                    <Notification
                        message={notificationMessage}
                        type="success"
                        onClose={handleCloseNotification}
                        duration={2000}
                    />
                )}

                <div className="search-bar-container">
                    <input
                        type="text"
                        className="search-input"
                        placeholder="Hledat autory podle jména..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <svg
                        className="search-icon"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                    >
                        <circle cx="11" cy="11" r="8"></circle>
                        <path d="m21 21-4.35-4.35"></path>
                    </svg>
                </div>

                {error && (
                    <div className="alert alert-error">
                        {error}
                    </div>
                )}

                {loading ? (
                    <div className="loading-container">
                        <p>Načítání autorů...</p>
                    </div>
                ) : authors.length === 0 ? (
                    <div className="no-authors">
                        <p>Zatím nejsou žádní autoři v databázi.</p>
                    </div>
                ) : (
                    <div className="authors-list">
                        {authors.map(author => (
                            <div key={author.id} className="author-item">
                                <div className="author-info">
                                    <h3 className="author-name">{author.name}</h3>
                                </div>
                                <div className="author-actions">
                                    <button
                                        className="btn-action btn-edit"
                                        onClick={() => handleEdit(author.id)}
                                        title="Upravit autora"
                                    >
                                        ✏️ Upravit
                                    </button>
                                    <button
                                        className="btn-action btn-delete"
                                        onClick={() => handleDeleteClick(author)}
                                        title="Smazat autora"
                                    >
                                        🗑️ Smazat
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {!loading && !error && (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalElements={totalElements}
                        onPageChange={handlePageChange}
                        itemLabel="autorů"
                    />
                )}

                {/* Delete Confirmation Modal */}
                {deleteConfirm && (
                    <div className="modal-overlay" onClick={handleDeleteCancel}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <h2 className="modal-title">Potvrdit smazání</h2>
                            <p className="modal-text">
                                Opravdu chcete smazat autora <strong>{deleteConfirm.name}</strong>?
                            </p>
                            <p className="modal-warning">
                                Tato akce je nevratná!
                            </p>
                            <div className="modal-actions">
                                <button
                                    className="btn btn-secondary"
                                    onClick={handleDeleteCancel}
                                    disabled={deleting}
                                >
                                    Zrušit
                                </button>
                                <button
                                    className="btn btn-danger"
                                    onClick={handleDeleteConfirm}
                                    disabled={deleting}
                                >
                                    {deleting ? 'Mazání...' : 'Smazat'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ManageAuthors;
